import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, FileText, Trash2, Edit, X } from 'lucide-react';
import { toast } from 'sonner';

const FIELD_TYPES = [
  { value: 'text', label: 'Texto' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Teléfono' },
  { value: 'number', label: 'Número' },
  { value: 'date', label: 'Fecha' },
  { value: 'textarea', label: 'Texto largo' },
  { value: 'select', label: 'Selector' }
];

const TARGETS = [
  { value: 'user', label: 'Creación de Usuario' },
  { value: 'client', label: 'Creación de Cliente' },
  { value: 'report', label: 'Reporte personalizado' }
];

export default function Templates() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', target: 'user', fields: [] });

  const isMaster = user?.role_id === 'master' || user?.role_id === 'superadmin';

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await api.getTemplates();
      setTemplates(response.data);
    } catch (error) {
      toast.error('Error al cargar plantillas');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ name: '', target: 'user', fields: [] });
    setEditingId(null);
  };

  const openCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (tpl) => {
    setEditingId(tpl.template_id);
    setForm({
      name: tpl.name,
      target: tpl.target,
      fields: tpl.fields.map(f => ({ ...f }))
    });
    setDialogOpen(true);
  };

  const addField = () => {
    setForm({
      ...form,
      fields: [...form.fields, { label: '', field_type: 'text', required: false, options: [], placeholder: '' }]
    });
  };

  const updateField = (idx, patch) => {
    const newFields = [...form.fields];
    newFields[idx] = { ...newFields[idx], ...patch };
    setForm({ ...form, fields: newFields });
  };

  const removeField = (idx) => {
    setForm({ ...form, fields: form.fields.filter((_, i) => i !== idx) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    if (form.fields.length === 0) {
      toast.error('Agrega al menos un campo');
      return;
    }
    const payload = {
      name: form.name,
      target: form.target,
      fields: form.fields.map(f => ({
        label: f.label,
        field_type: f.field_type,
        required: !!f.required,
        options: typeof f.options === 'string'
          ? f.options.split(',').map(s => s.trim()).filter(Boolean)
          : (f.options || []),
        placeholder: f.placeholder || null
      }))
    };
    try {
      if (editingId) {
        await api.updateTemplate(editingId, payload);
        toast.success('Plantilla actualizada');
      } else {
        await api.createTemplate(payload);
        toast.success('Plantilla creada');
      }
      setDialogOpen(false);
      resetForm();
      loadTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar plantilla');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta plantilla?')) return;
    try {
      await api.deleteTemplate(id);
      toast.success('Plantilla eliminada');
      loadTemplates();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  if (!isMaster) {
    return (
      <div className="text-center py-12" data-testid="templates-no-access">
        <FileText size={48} className="mx-auto text-gray-400 mb-4" />
        <p className="text-gray-600">Solo el rol Master puede gestionar plantillas</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full" data-testid="templates-loading">Cargando...</div>;
  }

  return (
    <div className="space-y-6" data-testid="templates-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#022C22]">Plantillas Editables</h1>
          <p className="text-[#64748B] mt-1">Define campos personalizados para usuarios, clientes y reportes</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} data-testid="add-template-button">
              <Plus size={18} className="mr-2" />
              Nueva Plantilla
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Editar plantilla' : 'Nueva plantilla'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nombre</Label>
                  <Input
                    data-testid="template-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Plantilla Vendedor"
                    required
                  />
                </div>
                <div>
                  <Label>Aplicar a</Label>
                  <select
                    data-testid="template-target-select"
                    value={form.target}
                    onChange={(e) => setForm({ ...form, target: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {TARGETS.map(t => (<option key={t.value} value={t.value}>{t.label}</option>))}
                  </select>
                </div>
              </div>

              <div className="border-t border-[#E2E8F0] pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold">Campos del formulario</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addField} data-testid="add-field-button">
                    <Plus size={14} className="mr-1" /> Agregar campo
                  </Button>
                </div>
                <div className="space-y-3">
                  {form.fields.map((field, idx) => (
                    <Card key={idx} className="border border-[#E2E8F0]" data-testid={`field-row-${idx}`}>
                      <CardContent className="pt-4 space-y-2">
                        <div className="grid grid-cols-12 gap-2 items-end">
                          <div className="col-span-5">
                            <Label className="text-xs">Etiqueta</Label>
                            <Input
                              data-testid={`field-label-${idx}`}
                              value={field.label}
                              onChange={(e) => updateField(idx, { label: e.target.value })}
                              placeholder="Ej. Cédula"
                              required
                            />
                          </div>
                          <div className="col-span-3">
                            <Label className="text-xs">Tipo</Label>
                            <select
                              data-testid={`field-type-${idx}`}
                              value={field.field_type}
                              onChange={(e) => updateField(idx, { field_type: e.target.value })}
                              className="w-full px-2 py-2 border border-[#E2E8F0] rounded-md text-sm"
                            >
                              {FIELD_TYPES.map(t => (<option key={t.value} value={t.value}>{t.label}</option>))}
                            </select>
                          </div>
                          <div className="col-span-3 flex items-center gap-2 pb-2">
                            <input
                              type="checkbox"
                              id={`req-${idx}`}
                              checked={!!field.required}
                              onChange={(e) => updateField(idx, { required: e.target.checked })}
                              data-testid={`field-required-${idx}`}
                            />
                            <Label htmlFor={`req-${idx}`} className="text-xs cursor-pointer">Requerido</Label>
                          </div>
                          <div className="col-span-1 flex justify-end pb-2">
                            <Button type="button" size="icon" variant="ghost" onClick={() => removeField(idx)} data-testid={`remove-field-${idx}`}>
                              <X size={16} className="text-red-600" />
                            </Button>
                          </div>
                        </div>
                        {field.field_type === 'select' && (
                          <div>
                            <Label className="text-xs">Opciones (separadas por coma)</Label>
                            <Input
                              data-testid={`field-options-${idx}`}
                              value={Array.isArray(field.options) ? field.options.join(', ') : (field.options || '')}
                              onChange={(e) => updateField(idx, { options: e.target.value })}
                              placeholder="Norte, Sur, Este, Oeste"
                            />
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                  {form.fields.length === 0 && (
                    <p className="text-sm text-[#94A3B8] text-center py-4">Aún no hay campos. Pulsa "Agregar campo".</p>
                  )}
                </div>
              </div>

              <Button type="submit" className="w-full" data-testid="submit-template-button">
                {editingId ? 'Guardar cambios' : 'Crear plantilla'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((tpl) => (
          <Card key={tpl.template_id} className="border border-[#E2E8F0] shadow-sm" data-testid={`template-card-${tpl.template_id}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="font-heading text-lg font-bold">{tpl.name}</CardTitle>
                  <p className="text-xs text-[#94A3B8] mt-1">
                    {TARGETS.find(t => t.value === tpl.target)?.label || tpl.target}
                  </p>
                </div>
                <FileText size={20} className="text-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 mb-4">
                <p className="text-sm text-[#64748B]">{tpl.fields.length} campo(s):</p>
                <div className="flex flex-wrap gap-1">
                  {tpl.fields.slice(0, 6).map((f, i) => (
                    <span key={i} className="px-2 py-0.5 bg-[#F1F5F9] rounded text-xs text-[#022C22]">
                      {f.label}
                    </span>
                  ))}
                  {tpl.fields.length > 6 && (
                    <span className="px-2 py-0.5 text-xs text-[#94A3B8]">+{tpl.fields.length - 6}</span>
                  )}
                </div>
              </div>
              <div className="flex gap-2 pt-2 border-t border-[#E2E8F0]">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(tpl)} data-testid={`edit-template-${tpl.template_id}`}>
                  <Edit size={14} className="mr-1" /> Editar
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleDelete(tpl.template_id)} data-testid={`delete-template-${tpl.template_id}`}>
                  <Trash2 size={14} className="text-red-600" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {templates.length === 0 && (
        <div className="text-center py-12" data-testid="no-templates-message">
          <FileText size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600">Aún no hay plantillas. Crea la primera para personalizar formularios.</p>
        </div>
      )}
    </div>
  );
}
